import {
  formatFollowerBoundary,
  type PlatformSponsorshipSettings,
  type SponsorshipCategory,
} from '@inflip/contracts/admin';
import {
  SOCIAL_PLATFORM_LABELS,
  SPONSORSHIP_ELIGIBILITY_PLATFORMS,
  type SponsorshipEligibilityPlatform,
} from '@inflip/shared';
import { useEffect, useState } from 'react';

import { Field, SavedAt, usePlatformSettings } from '../components/settings-form';
import { Button, ErrorBanner, LoadingRow, PageHeader, Section, inputCls } from '../components/ui';
import { api } from '../lib/api-client';

/** 폼 상태 — 입력 중 빈 칸을 허용하기 위해 기준 수치는 문자열로 든다 */
type SponsorshipForm = Record<SponsorshipEligibilityPlatform, string> & {
  categories: SponsorshipCategory[];
};

/** 새 종목의 id — 협찬에 저장되는 값이라 이름과 무관하게 한 번 정해지면 바뀌지 않는다 */
function newCategoryId(): string {
  return `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

const UNIT: Record<SponsorshipEligibilityPlatform, string> = {
  YOUTUBE: '구독자',
  INSTAGRAM: '팔로워',
  TIKTOK: '팔로워',
  THREADS: '팔로워',
  SOOP: '팔로워',
  PANDATV: '팔로워',
};

/** 채널별 값 하나씩으로 객체를 만든다 — 채널이 늘어도 폼 코드를 건드리지 않게 */
function perPlatform<T>(
  of: (platform: SponsorshipEligibilityPlatform) => T,
): Record<SponsorshipEligibilityPlatform, T> {
  return Object.fromEntries(SPONSORSHIP_ELIGIBILITY_PLATFORMS.map((p) => [p, of(p)])) as Record<
    SponsorshipEligibilityPlatform,
    T
  >;
}

/** 협찬 설정 — 열람·지원 기준(채널 중 하나만 넘으면 통과)과 협찬 종목 */
export function SponsorshipSettingsPage() {
  const { session, settings, error, setError, run } = usePlatformSettings();
  const [form, setForm] = useState<SponsorshipForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (settings && !form) {
      const m = settings.sponsorship.minFollowers;
      setForm({
        ...perPlatform((platform) => String(m[platform])),
        categories: settings.sponsorship.categories,
      });
    }
  }, [settings, form]);

  if (!session) return null;

  const toCount = (value: string) => Math.max(0, Math.floor(Number(value)) || 0);

  const save = async () => {
    if (!form || saving) return;
    const categories = form.categories.map((c) => ({ id: c.id, label: c.label.trim() }));
    if (categories.length === 0 || categories.some((c) => !c.label)) {
      setError('종목은 1개 이상 있어야 하고, 이름을 비워 둘 수 없어요.');
      return;
    }
    if (new Set(categories.map((c) => c.label)).size !== categories.length) {
      setError('같은 이름의 종목이 있어요.');
      return;
    }
    const body: PlatformSponsorshipSettings = {
      minFollowers: perPlatform((platform) => toCount(form[platform])),
      categories,
    };
    setSaving(true);
    setSavedAt(null);
    const ok = await run(() => api.updateSponsorshipSettings(session.accessToken, body));
    if (ok) {
      setSavedAt(new Date());
      setForm({
        ...perPlatform((platform) => String(body.minFollowers[platform])),
        categories: body.categories,
      });
    }
    setSaving(false);
  };

  return (
    <main className="mx-auto w-full max-w-xl px-6 py-8">
      <PageHeader
        title="협찬 설정"
        description="협찬을 볼 수 있는 인플루언서 기준과 협찬 종목을 관리해요 — 저장 즉시 반영돼요"
        actions={
          <>
            <SavedAt at={savedAt} />
            <Button disabled={saving || !form} onClick={() => void save()}>
              {saving ? '저장 중…' : '저장'}
            </Button>
          </>
        }
      />
      {error && <ErrorBanner message={error} />}
      {!form && !error && <LoadingRow />}

      {form && (
        <>
          <Section title="열람·지원 기준">
            <p className="text-xs text-muted-foreground">
              협찬 목록은 누구나 볼 수 있지만, 상세 내용과 지원은 아래 기준을 넘는 인증 인플루언서만
              할 수 있어요. 인증된 채널 중 <b>하나만</b> 기준 이상이면 통과해요. 협찬을 올린 업체는
              자기 협찬을 항상 볼 수 있어요.
            </p>
            <div className="mt-4 space-y-3">
              {SPONSORSHIP_ELIGIBILITY_PLATFORMS.map((platform) => {
                const count = toCount(form[platform]);
                return (
                  <Field
                    key={platform}
                    label={`${SOCIAL_PLATFORM_LABELS[platform]} 최소 ${UNIT[platform]} 수`}
                    hint={
                      count > 0
                        ? `${SOCIAL_PLATFORM_LABELS[platform]} ${UNIT[platform]} ${formatFollowerBoundary(count)} 인증 채널이 있으면 통과해요`
                        : `0 이면 ${SOCIAL_PLATFORM_LABELS[platform]} 채널 인증만 돼 있어도 통과해요`
                    }
                  >
                    <input
                      type="number"
                      min={0}
                      className={`w-36 ${inputCls}`}
                      value={form[platform]}
                      onChange={(e) => setForm({ ...form, [platform]: e.target.value })}
                    />
                  </Field>
                );
              })}
            </div>
          </Section>
          <Section title="협찬 종목">
            <p className="text-xs text-muted-foreground">
              협찬을 올릴 때 하나를 고르고, 협찬 목록에서 종목별로 걸러 볼 수 있어요. 위에서부터
              순서대로 표시돼요. 종목을 지워도 이미 올라온 협찬은 남고, 종목 표시만 빠져요.
            </p>
            <div className="mt-3 space-y-2">
              {form.categories.map((category, i) => (
                <div key={category.id} className="flex items-center gap-2">
                  <input
                    className={`w-56 ${inputCls}`}
                    placeholder="종목 이름"
                    maxLength={20}
                    value={category.label}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        categories: form.categories.map((c, j) =>
                          j === i ? { ...c, label: e.target.value } : c,
                        ),
                      })
                    }
                  />
                  <button
                    type="button"
                    aria-label="위로"
                    disabled={i === 0}
                    onClick={() => {
                      const next = [...form.categories];
                      [next[i - 1], next[i]] = [next[i]!, next[i - 1]!];
                      setForm({ ...form, categories: next });
                    }}
                    className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition hover:bg-muted disabled:opacity-30"
                  >
                    <svg
                      viewBox="0 0 12 12"
                      className="h-3 w-3"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M2.5 7.5 6 4l3.5 3.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    aria-label="종목 삭제"
                    onClick={() =>
                      setForm({ ...form, categories: form.categories.filter((_, j) => j !== i) })
                    }
                    className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-red-600"
                  >
                    <svg
                      viewBox="0 0 12 12"
                      className="h-3 w-3"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M3 3l6 6M9 3l-6 6" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            <Button
              variant="outline"
              className="mt-3"
              disabled={form.categories.length >= 30}
              onClick={() =>
                setForm({
                  ...form,
                  categories: [...form.categories, { id: newCategoryId(), label: '' }],
                })
              }
            >
              종목 추가
            </Button>
          </Section>
        </>
      )}
    </main>
  );
}
